import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, X, TrendingUp, AlertCircle, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../api/client';

type Notification = {
  id: number;
  type: 'niche_discovered' | 'system' | 'alert';
  title: string;
  message: string | null;
  metadata: string | null;
  read: number;
  created_at: string;
};

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications.list(),
    refetchInterval: 60000, // Refetch every minute
  });

  const markAsRead = useMutation({
    mutationFn: (id: number) => api.notifications.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: () => api.notifications.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const notifications = data?.notifications ?? [];
  const unreadCount = notifications.filter((n: Notification) => !n.read).length;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getIcon = (type: string) => {
    switch (type) {
      case 'niche_discovered':
        return TrendingUp;
      case 'alert':
        return AlertCircle;
      default:
        return CheckCircle;
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-gray-800 rounded-lg transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5 text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-etsy-orange text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed left-2 right-2 sm:left-[270px] sm:right-auto top-16 sm:top-12 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[60] overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllAsRead.mutate()}
                  className="text-xs text-etsy-orange hover:text-orange-600 font-medium"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.slice(0, 10).map((notification: Notification) => {
                  const Icon = getIcon(notification.type);
                  const metadata = notification.metadata ? JSON.parse(notification.metadata) : null;

                  return (
                    <div
                      key={notification.id}
                      className={clsx(
                        'px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group',
                        !notification.read && 'bg-orange-50/50'
                      )}
                      onClick={() => {
                        if (!notification.read) {
                          markAsRead.mutate(notification.id);
                        }
                        // Navigate to research page for niche notifications
                        if (notification.type === 'niche_discovered') {
                          setIsOpen(false);
                          // Navigate to research with keyword search if available
                          if (metadata?.keyword) {
                            navigate(`/research?search=${encodeURIComponent(metadata.keyword)}`);
                          } else {
                            navigate('/research');
                          }
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={clsx(
                            'p-1.5 rounded-full flex-shrink-0',
                            notification.type === 'niche_discovered'
                              ? 'bg-green-100 text-green-600'
                              : notification.type === 'alert'
                                ? 'bg-red-100 text-red-600'
                                : 'bg-blue-100 text-blue-600'
                          )}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <p className="text-sm font-medium text-gray-900 flex-1">
                              {notification.title}
                            </p>
                            <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                              {!notification.read && (
                                <span className="w-2 h-2 bg-etsy-orange rounded-full" />
                              )}
                              {notification.type === 'niche_discovered' && (
                                <ExternalLink className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                          </div>
                          {notification.message && (
                            <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          )}
                          {metadata?.trendScore && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-medium text-green-600">
                                Score: {Math.round(metadata.trendScore * 100)}%
                              </span>
                              {metadata.source && (
                                <span className="text-xs text-gray-400">via {metadata.source}</span>
                              )}
                            </div>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">
                            {formatTime(notification.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 10 && (
            <div className="px-4 py-2 border-t border-gray-100">
              <Link
                to="/research"
                onClick={() => setIsOpen(false)}
                className="text-xs text-etsy-orange hover:text-orange-600 font-medium"
              >
                View all in Research →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
